/**
 * Gateway Client Configuration
 * T010: Implement GatewayConfig and createConfig()
 */

import type { ApiError } from './errors'

// ============================================================================
// Types
// ============================================================================

export type RequestInterceptor = (
  request: Request
) => Request | Promise<Request>
export type ResponseInterceptor = (
  response: Response
) => Response | Promise<Response>
export type ErrorInterceptor = (error: ApiError) => void

export interface GatewayConfig {
  /** Gateway base URL */
  readonly baseUrl: string
  /** Request timeout in milliseconds (default: 30000) */
  readonly timeout: number
  /** Authentication mode */
  readonly authMode: 'apiKey' | 'oauth'
  /** API key for bearer token auth */
  readonly apiKey?: string
  /** Pre-request interceptor */
  readonly onRequest?: RequestInterceptor
  /** Post-response interceptor */
  readonly onResponse?: ResponseInterceptor
  /** Error handler */
  readonly onError?: ErrorInterceptor
}

export interface GatewayConfigInput {
  /** Gateway base URL (required) */
  baseUrl: string
  /** Request timeout in milliseconds (default: 30000) */
  timeout?: number
  /** Authentication mode (default: 'oauth') */
  authMode?: 'apiKey' | 'oauth'
  /** API key for bearer token auth */
  apiKey?: string
  /** Pre-request interceptor */
  onRequest?: RequestInterceptor
  /** Post-response interceptor */
  onResponse?: ResponseInterceptor
  /** Error handler */
  onError?: ErrorInterceptor
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_TIMEOUT = 300000 // 5 minutes — actions like image-upscale can be slow
const DEFAULT_AUTH_MODE = 'oauth' as const

// ============================================================================
// Functions
// ============================================================================

// Declare the global __GATEWAY_URL__ defined in vite.config.ts
declare const __GATEWAY_URL__: string

/**
 * Get the default base URL from environment variables
 */
export function getDefaultBaseUrl(): string {
  // Use the explicitly defined gateway URL from vite.config.ts
  // This ensures the value is statically replaced at build time
  if (typeof __GATEWAY_URL__ !== 'undefined' && __GATEWAY_URL__) {
    return __GATEWAY_URL__
  }

  // For local development with Vite proxy, use same origin (empty string)
  // The vite.config.ts proxies /v1 and /auth to localhost:8081
  return ''
}

/**
 * Create a frozen gateway configuration object
 */
export function createConfig(input: GatewayConfigInput): GatewayConfig {
  const config: GatewayConfig = {
    baseUrl: normalizeBaseUrl(input.baseUrl),
    timeout: input.timeout ?? DEFAULT_TIMEOUT,
    authMode: input.authMode ?? DEFAULT_AUTH_MODE,
    apiKey: input.apiKey,
    onRequest: input.onRequest,
    onResponse: input.onResponse,
    onError: input.onError,
  }

  // Freeze the config to prevent mutations
  return Object.freeze(config)
}

/**
 * Create a config with default base URL from environment
 */
export function createDefaultConfig(
  overrides: Partial<GatewayConfigInput> = {}
): GatewayConfig {
  return createConfig({
    baseUrl: getDefaultBaseUrl(),
    ...overrides,
  })
}

/**
 * Normalize base URL by removing trailing slashes
 */
function normalizeBaseUrl(url: string): string {
  return url.replace(/\/+$/, '')
}

/**
 * Validate API key format (verts_ prefix + 32 alphanumeric chars)
 */
export function isValidApiKey(key: string): boolean {
  return /^verts_[a-zA-Z0-9]{32}$/.test(key)
}
